import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddTrialNotification from "../../components/Modals/AddTrialNotification";
import ShowNotificationModal from "../../components/Modals/ShowNotificationModal";
import DeleteMessage from "../../components/Modals/DeleteMessage";
import { __API_URL__ } from "../../SERVER_URL";
import Loader from "../../components/Loader";
import { LoaderContext } from "../../context/LoaderContext";
import { useEcrfContext } from "../../context/EcrfContext";
import { USER_ROLES } from "../../utils/constants";
import { formatHoursDuration } from "../../utils/helpers";

dayjs.extend(utc);

const TrialNotification = (props) => {
  const { trialId } = props;

  const [modalShow, setModalShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [deleteObj, setDeleteObj] = useState(false);
  const [showAddNotification, setShowAddNotification] = useState(false);

  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;
  const [trialNotification, setTrialNotification] = useState([]);
  const [adverseNotification, setAdverseNotification] = useState([]);
  const [ediaryNotification, setEdiaryNotification] = useState([]);
  const [notificationToDeleteId, setNotificationToDeleteId] = useState();
  const [selectedNotification, setSelectedNotification] = useState("Trial");
  const loaderContext = useContext(LoaderContext);
  const { trial } = useEcrfContext();
  const hasEdiary = Boolean(trial?.ediary_config);

  const convertFirstLetterToCapital = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleDeleteNotification = () => {
    axios
      .delete(`${__API_URL__}/api/appointments/delete_trial_notification/${selectedItem.id}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then(() => {
        setModalShow(false);
        toast.success("Notification Deleted Successfully");
        getTrialsNotification();
        getAdverseNotification();
        getEdiaryNotification();
      })
      .catch(() => {
        toast.error("Something Went Wrong");
      });
  };

  const NotificationDelete = (id) => {
    setNotificationToDeleteId(id);
    setDeleteObj(true);
  };

  const getTrialsNotification = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/appointments/get_trial_notifications/${trialId}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setTrialNotification(res.data))
      .catch(() => {})
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const getAdverseNotification = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/appointments/get_adverse_notifications/${trialId}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setAdverseNotification(res.data))
      .catch(() => {})
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const getEdiaryNotification = () => {
    if (!hasEdiary) return;
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/appointments/get_ediary_notifications/${trialId}/`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((res) => setEdiaryNotification(res.data))
      .catch(() => {})
      .finally(() => loaderContext.setLoaderCount((prev) => prev - 1));
  };

  const confirmDelete = () => {
    if (notificationToDeleteId) {
      axios
        .delete(
          `${__API_URL__}/api/appointments/delete_trial_notification/${notificationToDeleteId}/`,
          { headers: { Authorization: `Token ${token}` } }
        )
        .then(() => {
          toast.success("Notification Deleted Successfully");
          getTrialsNotification();
          getAdverseNotification();
          getEdiaryNotification();
          setNotificationToDeleteId(0);
        })
        .catch(() => {
          toast.error("Something Went Wrong");
        })
        .finally(() => {
          setDeleteObj(false);
        });
    }
  };

  useEffect(() => {
    if (selectedNotification === "Trial") {
      getTrialsNotification();
    } else if (selectedNotification === "Adverse") {
      getAdverseNotification();
    } else if (selectedNotification === "EDiary") {
      getEdiaryNotification();
    }
    setCurrentPage(1);
  }, [selectedNotification]);

  const notificationsBySelection = {
    Trial: trialNotification,
    Adverse: adverseNotification,
    EDiary: ediaryNotification,
  };
  const notifications = notificationsBySelection[selectedNotification];
  const usesVisitColumns = selectedNotification !== "Adverse";
  const totalCount = notifications.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentNotifications = notifications.slice(startIndex, startIndex + itemsPerPage);
  const isAdmin =
    role === USER_ROLES.CRO ||
    role === USER_ROLES.CRO_COORDINATOR ||
    role === USER_ROLES.DATA_MANAGER ||
    role === USER_ROLES.SUPER_ADMIN;

  const headerCellSx = {
    backgroundColor: (theme) => theme.palette.grey[100],
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: "0.85rem",
    color: "#424242",
    whiteSpace: "nowrap",
  };

  const bodyCellSx = { whiteSpace: "nowrap" };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Visit Related Notifications
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant={selectedNotification === "Trial" ? "contained" : "outlined"}
            size="small"
            disabled={loaderContext.loaderCount > 0}
            onClick={() => setSelectedNotification("Trial")}
          >
            Trial Notification
          </Button>
          <Button
            variant={selectedNotification === "Adverse" ? "contained" : "outlined"}
            size="small"
            disabled={loaderContext.loaderCount > 0}
            onClick={() => setSelectedNotification("Adverse")}
          >
            Adverse Notification
          </Button>
          {hasEdiary && (
            <Button
              variant={selectedNotification === "EDiary" ? "contained" : "outlined"}
              size="small"
              disabled={loaderContext.loaderCount > 0}
              onClick={() => setSelectedNotification("EDiary")}
            >
              eDiary Notification
            </Button>
          )}
        </Stack>
        {role !== "Reviewer" && isAdmin && (
          <Button
            variant="contained"
            size="small"
            data-testid="add-notification-btn"
            disabled={loaderContext.loaderCount > 0}
            onClick={() => setShowAddNotification(true)}
          >
            Add Notification
          </Button>
        )}
      </Box>

      <AddTrialNotification
        token={token}
        trialId={trialId}
        getTrialsNotification={getTrialsNotification}
        getAdverseNotification={getAdverseNotification}
        getEdiaryNotification={getEdiaryNotification}
        hasEdiary={hasEdiary}
        open={showAddNotification}
        onClose={() => setShowAddNotification(false)}
      />

      {modalShow && (
        <ShowNotificationModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          selectedItem={selectedItem}
          handleDelete={handleDeleteNotification}
        />
      )}

      {loaderContext.loaderCount > 0 ? (
        <Loader />
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "50vh", mt: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {usesVisitColumns ? (
                    <>
                      <TableCell sx={headerCellSx}>Custom Title</TableCell>
                      <TableCell sx={headerCellSx}>Custom Data</TableCell>
                      <TableCell sx={headerCellSx}>In App</TableCell>
                      <TableCell sx={headerCellSx}>SMS</TableCell>
                      <TableCell sx={headerCellSx}>Email</TableCell>
                      <TableCell sx={headerCellSx}>Hours Before</TableCell>
                      <TableCell sx={headerCellSx}>Hours After</TableCell>
                      <TableCell sx={headerCellSx}>Recurring Hours After</TableCell>
                      {isAdmin && <TableCell sx={headerCellSx}>Delete</TableCell>}
                    </>
                  ) : (
                    <>
                      <TableCell sx={headerCellSx}>Custom Title</TableCell>
                      <TableCell sx={headerCellSx}>Custom Data</TableCell>
                      <TableCell sx={headerCellSx}>In App</TableCell>
                      <TableCell sx={headerCellSx}>SMS</TableCell>
                      <TableCell sx={headerCellSx}>Email</TableCell>
                      <TableCell sx={headerCellSx}>Start Date</TableCell>
                      <TableCell sx={headerCellSx}>End Date</TableCell>
                      <TableCell sx={headerCellSx}>Start Date Offset</TableCell>
                      <TableCell sx={headerCellSx}>End Date Offset</TableCell>
                      <TableCell sx={headerCellSx}>Frequency</TableCell>
                      {isAdmin && <TableCell sx={headerCellSx}>Delete</TableCell>}
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {usesVisitColumns
                  ? currentNotifications.map((notification) => (
                      <TableRow
                        key={notification.id}
                        hover
                        data-testid="notif-row"
                        data-title={notification.custom_title}
                      >
                        <TableCell sx={bodyCellSx}>{notification.custom_title}</TableCell>
                        <TableCell sx={bodyCellSx}>{notification.custom_data}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${notification.is_app}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${notification.is_sms}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${notification.is_email}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} data-field="hours_before">
                          {formatHoursDuration(notification.hours_before)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} data-field="hours_after">
                          {formatHoursDuration(notification.hours_after)}
                        </TableCell>
                        <TableCell sx={bodyCellSx} data-field="after_visit_frequency_hours">
                          {formatHoursDuration(notification.after_visit_frequency_hours)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell sx={bodyCellSx}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => NotificationDelete(notification.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  : currentNotifications.map((adverse) => (
                      <TableRow key={adverse.id} hover>
                        <TableCell sx={bodyCellSx}>{adverse.custom_title}</TableCell>
                        <TableCell sx={bodyCellSx}>{adverse.custom_data}</TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${adverse.is_app}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${adverse.is_sms}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {convertFirstLetterToCapital(`${adverse.is_email}`)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {adverse.adverse_start_date
                            ? dayjs.utc(adverse.adverse_start_date).local().format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {adverse.adverse_end_date
                            ? dayjs.utc(adverse.adverse_end_date).local().format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatHoursDuration(adverse.adverse_start_date_offset)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatHoursDuration(adverse.adverse_end_date_offset)}
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          {formatHoursDuration(adverse.adverse_frequency)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell sx={bodyCellSx}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => NotificationDelete(adverse.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {totalCount > 0 ? startIndex + 1 : 0} out of {totalCount}
            </Typography>
            {totalCount > itemsPerPage && (
              <Pagination
                count={Math.ceil(totalCount / itemsPerPage)}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
              />
            )}
          </Box>
        </>
      )}

      {deleteObj && <DeleteMessage setDeleteObj={setDeleteObj} confirmDelete={confirmDelete} />}
    </Box>
  );
};

export default TrialNotification;
